import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import moment from 'moment-timezone';

import { ChartService } from './chartService';
import { FilterSetup } from './filterService';
import {
  getTuhonoFilterDateRange,
  mataurangaMatchesSearch,
  TuhonoFilterDateRange,
} from '../helpers/tuhonoFilterHelpers';

export interface FilterOptionCounts {
  matauranga: Record<string, number>;
  projects: Record<string, number>;
  locations: Record<string, number>;
}

export const EMPTY_FILTER_OPTION_COUNTS: FilterOptionCounts = {
  matauranga: {},
  projects: {},
  locations: {},
};

const PROJECT_FIELD_IDS = ['project-name', 'project-name-multi'];
const COUNT_REQUEST_TIMEOUT_MS = 15000;

/**
 * Count unique mātauranga entries per domain. The API returns every mātauranga
 * record plus the days it applies to, so an entry that spans several days is
 * still only counted once.
 */
export const buildMataurangaCounts = (response: any, searchTerm: string): Record<string, number> => {
  const items = Array.isArray(response?.matauranga) ? response.matauranga : [];
  const days = Array.isArray(response?.daysWithMatauranga) ? response.daysWithMatauranga : [];
  const seen = new Set<string>();
  const counts: Record<string, number> = {};

  days.forEach((day: any) => {
    (Array.isArray(day?.matauranga) ? day.matauranga : []).forEach((index: number) => {
      const item = items[index];
      if (!item || !mataurangaMatchesSearch(item, searchTerm)) return;

      const itemKey = String(item.id ?? index);
      if (seen.has(itemKey)) return;
      seen.add(itemKey);

      const domain = String(item.domain_type || '')
        .trim()
        .toLowerCase();
      if (!domain) return;

      counts[domain] = (counts[domain] || 0) + 1;
    });
  });

  return counts;
};

const getObservations = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.observations)) return response.observations;
  return [];
};

/**
 * Count observations per contributing project list item.
 *
 * Tūhono's observation endpoint returns database-shaped items (`field_id`,
 * `scalar_value`, `array_value`) while other endpoints return the translated
 * shape (`fieldId`, `values`). Support both.
 */
export const buildProjectCounts = (response: any): Record<string, number> => {
  const observations = getObservations(response);
  const seen = new Set<string>();
  const counts: Record<string, number> = {};

  observations.forEach((observation: any) => {
    const observationKey = String(observation?.id ?? '');
    const items = Array.isArray(observation?.items) ? observation.items : [];

    items.forEach((item: any) => {
      if (!PROJECT_FIELD_IDS.includes(String(item?.fieldId ?? item?.field_id))) return;

      const translatedIds = (Array.isArray(item?.values) ? item.values : [])
        .map((value: any) => value?.id)
        .filter((id: any) => id !== null && id !== undefined);
      const arrayValues = item?.arrayValue ?? item?.array_value;
      const rawIds = [
        item?.scalarValue ?? item?.scalar_value,
        ...(Array.isArray(arrayValues) ? arrayValues : []),
      ].filter((id: any) => id !== null && id !== undefined);

      [...translatedIds, ...rawIds].forEach((id: any) => {
        const projectKey = String(id);
        const uniqueKey = `${observationKey}:${projectKey}`;
        if (seen.has(uniqueKey)) return;
        seen.add(uniqueKey);

        counts[projectKey] = (counts[projectKey] || 0) + 1;
      });
    });
  });

  return counts;
};

/**
 * Count observations per observation location. The summary response only
 * carries the location name, so fall back to that when no id is available.
 */
export const buildLocationCounts = (response: any): Record<string, number> => {
  const observations = getObservations(response);
  const counts: Record<string, number> = {};

  observations.forEach((observation: any) => {
    const rawLocation = observation?.location;
    const locationKey =
      rawLocation && typeof rawLocation === 'object'
        ? rawLocation.id ?? rawLocation.name
        : typeof rawLocation === 'string' && rawLocation.trim() !== ''
        ? rawLocation
        : observation?.locationId;

    if (locationKey === null || locationKey === undefined || locationKey === '') return;

    const key = String(locationKey);
    counts[key] = (counts[key] || 0) + 1;
  });

  return counts;
};

@Injectable({
  providedIn: 'root',
})
export class FilterOptionCountsService {
  constructor(private chartService: ChartService) {}

  /**
   * Load option counts for the selected Tūhono calendar period. Counts
   * deliberately ignore the project/location/domain selections so choosing one
   * filter does not collapse the available options to that selection.
   */
  public getCounts(filters: FilterSetup): Observable<FilterOptionCounts> {
    const range = getTuhonoFilterDateRange(filters);

    return this.getCountsForRange(range, filters.searchTerm);
  }

  public getCountsForRange(range: TuhonoFilterDateRange, searchTerm: string): Observable<FilterOptionCounts> {
    const startDateTime = moment.tz(`${range.startDate} 00:00:00`, 'Pacific/Auckland').utc().toISOString();
    const endDateTime = moment.tz(`${range.endDate} 23:59:59`, 'Pacific/Auckland').utc().toISOString();

    return forkJoin({
      matauranga: this.chartService.getMatauranga(startDateTime, endDateTime).pipe(
        timeout(COUNT_REQUEST_TIMEOUT_MS),
        catchError(() => of(null))
      ),
      observations: this.getObservationResponse(range, searchTerm),
    }).pipe(
      map(({ matauranga, observations }) => ({
        matauranga: buildMataurangaCounts(matauranga, searchTerm),
        projects: buildProjectCounts(observations),
        locations: buildLocationCounts(observations),
      }))
    );
  }

  /**
   * Observation counts for the StreamDASH date range. Only the date range and
   * search term are applied so the project and location option counts stay
   * visible after a selection is made.
   */
  public getObservationCountsForRange(
    range: TuhonoFilterDateRange,
    searchTerm: string
  ): Observable<Pick<FilterOptionCounts, 'projects' | 'locations'>> {
    return this.getObservationResponse(range, searchTerm).pipe(
      map((response) => ({
        projects: buildProjectCounts(response),
        locations: buildLocationCounts(response),
      }))
    );
  }

  private getObservationResponse(range: TuhonoFilterDateRange, searchTerm: string): Observable<any> {
    return this.chartService
      .getObservationData(range.startDate, range.endDate, ['all'], searchTerm, {
        applyContributingFilters: false,
      })
      .pipe(
        timeout(COUNT_REQUEST_TIMEOUT_MS),
        catchError(() => of([]))
      );
  }
}
